import { useEffect, useState } from "react";

import withAuthenticator from "./hoc/withAuthenticator";
import Login from "./pages/Login/Login";
import LoadingDialog from "../src/components/Utils/LoadingDialog";

import "primeicons/primeicons.css";
import "./App.css";
import "./scss/styles.scss";
import "primereact/resources/themes/lara-light-blue/theme.css";
//import 'primeflex/primeflex.css'

import TagManager from "react-gtm-module";

import {
    UNSAFE_createBrowserHistory, UNSAFE_createRouter, UNSAFE_mapRouteProperties, unstable_RouterContextProvider
} from "react-router";
import { RouterProvider } from "react-router/dom";

import getRootRoutes from "@/routes/root";

import { putLog } from "./utils/logcache";

const TAG_MANAGER_ID = import.meta.env.VITE_GTM_CONTAINER_ID;
//ReactGA.initialize(TRACKING_ID);

const tagManagerArgs = {
    gtmId: TAG_MANAGER_ID
};
TagManager.initialize(tagManagerArgs);

const dynamicRoutes = [
    {
        id: "general",
        parent: "with-tos",
        getChildren: () => import("./routes/general.js")
    },
    {
        id: "billing",
        parent: "with-tos",
        getChildren: () => import("./routes/billing.js")
    },
    {
        id: "farms",
        parent: "with-tos",
        getChildren: () => import("./routes/farms.js")
    },
];

/**
 * This is a list of all dependencies that are dinamically imported somewhere in the app.
 * Preload then on init, so we cant fail later because the file doesnt exist anymore
 * */
const depsPreload = [
    () => import("exceljs"),
    () => import("file-saver"),
    () => import("xlsx")
];

const appBasename = undefined;

/**
 *
 * @param {ReturnType<typeof UNSAFE_createBrowserHistory>} history
 * @param {import("react-router").DataRouteObject[]} routes
 * @returns
 */
function createHistoryRouter(history, routes) {
    // for now, all route that could be added to the context are eternal. in the future, we should handle cleanups too
    const context = new unstable_RouterContextProvider();
    const addedDynamicRoutes = {};

    return UNSAFE_createRouter({
        history, routes,
        mapRouteProperties: UNSAFE_mapRouteProperties,
        basename: appBasename,
        unstable_getContext: () => context,
        async dataStrategy({
            matches
        }) {
            // Run middleware sequentially and let them add data to `context`
            let promError = null;
            let hasRedirect = false;
            const promises = []; // the other loaders will be started in the same sequence, but wont be waited for

            const isRedirectResult = (res) => (
                res.result?.status && (res.result.status >= 300 && res.result.status < 400)
            );

            const matchedToLoad = matches.filter(m => m.shouldLoad);
            for (const match of matchedToLoad) {
                let prom;
                if (hasRedirect) { // break early if we got a redirect
                    break;
                }

                (prom = match.resolve())
                    .then(res => {
                        if (isRedirectResult(res)) hasRedirect = true;
                    })
                    .catch(e => {
                        if (!promError) promError = e;
                    });
                promises.push(prom);

                // if we have wiseWait, await for these loaders first
                if (match.route.handle?.wiseWait) await prom;
                if (promError) throw promError;
            }

            // Run loaders in parallel
            return (await Promise.all(promises)).reduce((acc, result, i) =>
                Object.assign(acc, {
                    [matchedToLoad[i].route.id]: result,
                }),
            {});
        },
        async patchRoutesOnNavigation({ matches, patch }) {
            let leafRouteId = matches[matches.length - 1]?.route?.id;
            if (!leafRouteId) return;
            // hack: this doesnt handle very well the route with no added path
            if (leafRouteId === "root") leafRouteId = "with-tos";

            // maybe in the future we could add a route parameter to our dynamicRoutes, so we dont add all of the routes
            const childrenToPatch = dynamicRoutes.filter(cr => cr.parent === leafRouteId);
            if (!childrenToPatch.length) return;

            childrenToPatch.forEach(cr => {
                const uniqueId = `${cr.parent}-${cr.id}`;
                if (addedDynamicRoutes[uniqueId]) {
                    // // We should never have this condition. Maybe its not needed to throw an error, but be safe for now
                    // throw new Error("Route already added");
                }
                addedDynamicRoutes[uniqueId] = true;
            });

            const childenInfo = (await Promise.all(childrenToPatch.map(cr => cr.getChildren()))).map(r => r.default);
            patch(leafRouteId, Array.prototype.concat.call(...childenInfo));
        }
    }).initialize();
}

const getRouter = () => {
    const history = UNSAFE_createBrowserHistory({ window });
    const router = createHistoryRouter(history, getRootRoutes(history, depsPreload));

    if (!router.navigate.hack) {
        const navigate_orig = router.navigate;
        const navigate = (...params) => {
            putLog(JSON.parse(JSON.stringify(params)));
            navigate_orig(...params);
        };
        navigate.hack = true;
        router.navigate = navigate;
    }
    return router;
};

const App = () => {
    // loader functions will be called inmediatly when createBrowserRouter is called, if the route is correct
    // to prevent failing because we have no auth-data, defer the router creation to the mounting of the app component
    const [routerInfo, setRouterInfo] = useState({ router: null, idx: 0 });

    useEffect(() => {
        const router = getRouter();
        setRouterInfo((ri) => ({ router, idx: (ri.idx + 1) }));

        // we need to clear the memory of the previous router
        return () => {
            router.dispose();
        };
    }, []);

    if (!routerInfo.router) return null;
    return <RouterProvider
        key={routerInfo.idx} router={routerInfo.router}
    />;
};

export default withAuthenticator(App, Login, LoadingDialog);

// at least for now, preload all routes on application load
Promise.all(dynamicRoutes.map(cr => cr.getChildren())).catch(e => console.error(e));
